/**
 * MeetingDetailModal — slide-over panel (stile HubSpot/Pipedrive)
 *
 * Fix applicati:
 * - TaskModal: corrette prop (isOpen/onClose invece di open/onOpenChange)
 * - Follow-up meeting: chiude il detail prima di aprire SimpleMeetingModal
 * - Riferimenti: usa apiRequest (con JWT) invece di fetch nudo
 * - Modifica: MeetingDetailModal non ha campi editabili (titolo/orario/
 *   luogo/partecipanti) — "Modifica" riusa lo stesso pattern del follow-up
 *   per aprire SimpleMeetingModal in edit mode sul meeting corrente
 *   (initialData con l'id esistente -> isEditMode -> PATCH invece di POST)
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { meetingDetailLoadError } from "./meetingDetailLoadError";
import { entityDetailKey, invalidateMeetingViews } from "@/lib/queryKeys";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { VoiceAssistantTextarea } from "@/components/ui/VoiceAssistantTextarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { CrmRecordAutocomplete } from "@/components/ui/crm-record-autocomplete";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Calendar, Clock, MapPin, User, Building2, Briefcase,
  CheckCircle2, Clock3, XCircle, ChevronRight, Plus,
  Phone, Video, Users, Trash2, AlertCircle, Link2, Pencil, CalendarRange, UserPlus, Globe, Sparkles, Mail, Hotel
} from "lucide-react";
import { SimpleMeetingModal } from "./SimpleMeetingModal";
import TaskModal from "./TaskModal";
import DealModal from "./DealModal";
import { ToastAction } from "@/components/ui/toast";
import { MartinaOutputRating } from "@/components/exai/MartinaOutputRating";
import { buildMartinaCalendarMeetingTurnContext, useMartinaGlobal } from "@/contexts/ExAiGlobalContext";
import { operationalMeetingDescription } from "@shared/meetingDescription";
import { isTravelStayLike, travelStayBoundary } from "@shared/travelStay";
import { canCreateTravelWalletFromEvent, travelEventAppointmentWindow } from "@shared/travelWallet";
import { meetingAllDayBoundary } from "@shared/meetingAllDay";
import { meetingInviteResponseWindow } from "@/lib/meetingInviteResponseWindow";

// ─── Types ────────────────────────────────────────────────────────────────────

interface MeetingDetailModalProps {
  meetingId: number | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  active:    { label: "Pianificato",  color: "bg-blue-100 text-blue-800 border-blue-200",   icon: <Clock3 className="h-3 w-3" /> },
  scheduled: { label: "Pianificato",  color: "bg-blue-100 text-blue-800 border-blue-200",   icon: <Clock3 className="h-3 w-3" /> },
  completed: { label: "Completato",   color: "bg-green-100 text-green-800 border-green-200", icon: <CheckCircle2 className="h-3 w-3" /> },
  postponed: { label: "Posticipato",  color: "bg-amber-100 text-amber-800 border-amber-200", icon: <Clock className="h-3 w-3" /> },
  cancelled: { label: "Annullato",    color: "bg-red-100 text-red-800 border-red-200",       icon: <XCircle className="h-3 w-3" /> },
};

const TYPE_ICON: Record<string, React.ReactNode> = {
  "Call":      <Phone className="h-4 w-4" />,
  "In-Person": <Users className="h-4 w-4" />,
  "Video":     <Video className="h-4 w-4" />,
  "Virtual":   <Video className="h-4 w-4" />,
  "Evento":    <CalendarRange className="h-4 w-4" />,
  "Soggiorno": <Hotel className="h-4 w-4" />,
};

/**
 * Stato dell'invito ricevuto (mother-spec §13.2: richiesta di accordo, non
 * fatto). Colori dalle variabili di tema, mai hardcoded.
 */
const INVITE_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  needs_action: { label: "Da confermare", className: "bg-[color:var(--ma-amber-bg)] text-[color:var(--ma-amber)] border-[color:var(--ma-amber)]" },
  decision_pending: { label: "Partecipazione da decidere", className: "bg-violet-100 text-violet-800 border-violet-200" },
  accepted:     { label: "Confermato",    className: "bg-[color:var(--ma-green-bg)] text-[color:var(--ma-green)] border-[color:var(--ma-green)]" },
  tentative:    { label: "Forse",         className: "bg-[color:var(--ma-amber-bg)] text-[color:var(--ma-amber)] border-[color:var(--ma-amber)]" },
  declined:     { label: "Rifiutato",     className: "bg-[color:var(--ma-red-bg)] text-[color:var(--ma-red)] border-[color:var(--ma-red)]" },
  dismissed:    { label: "Chiuso senza risposta", className: "bg-muted text-muted-foreground border-border" },
};

const inviteConfirmationPhraseFor = (decision: 'accepted' | 'declined' | 'counter') => (
  decision === 'accepted' ? 'CONFERMO' : decision === 'declined' ? 'RIFIUTO' : 'PROPONGO'
);

/** Orario dello stesso istante in un altro fuso, per la riga di conversione. */
function timeInZone(iso: string, timeZone: string): string | null {
  try {
    return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone });
  } catch {
    // Un fuso non valido non deve rompere il pannello: si omette la riga.
    return null;
  }
}

/** Etichetta breve di un fuso IANA: "Asia/Kolkata" -> "Kolkata". */
function zoneLabel(timeZone: string): string {
  return timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone;
}

/**
 * `attendees.external` e' un array di OGGETTI {email, name, contactId}, non di
 * id. Qui si normalizza una volta sola, deduplicando per indirizzo.
 */
function externalAttendees(attendees: any): Array<{ email: string; name: string | null; contactId: number | null }> {
  const raw = Array.isArray(attendees?.external) ? attendees.external : [];
  const byAddress = new Map<string, { email: string; name: string | null; contactId: number | null }>();
  for (const person of raw) {
    if (!person || typeof person !== 'object') continue;
    const email = String((person as any).email ?? '').trim();
    if (!email) continue;
    const key = email.toLowerCase();
    if (byAddress.has(key)) continue;
    byAddress.set(key, {
      email,
      name: (person as any).name ?? null,
      contactId: Number.isSafeInteger(Number((person as any).contactId)) ? Number((person as any).contactId) : null,
    });
  }
  return [...byAddress.values()];
}

/**
 * Bug E2: il pannello mostrava "3 partecipanti" quando erano 2, perche'
 * sommava `internal` (id utente) a `external`, che nell'invito .ics contiene
 * la lista ATTENDEE completa — l'utente incluso. La somma era quindi sempre
 * un'inflazione. Quando l'elenco esterno esiste e' gia' completo e non va
 * sommato a nulla; `internal` conta solo per un meeting creato a mano, dove
 * l'elenco esterno e' vuoto.
 */
function countAttendees(attendees: any): number {
  const external = externalAttendees(attendees);
  if (external.length > 0) return external.length;
  return Array.isArray(attendees?.internal) ? attendees.internal.length : 0;
}

function canonicalMeetingParticipants(meeting: any): Array<{ label: string; email: string | null }> {
  if (Array.isArray(meeting?.participants) && meeting.participants.length > 0) {
    return meeting.participants.map((participant: any) => ({
      label: participant.displayName || participant.contactName || participant.email || 'Partecipante',
      email: participant.email || null,
    }));
  }
  return externalAttendees(meeting?.attendees).map((person) => ({
    label: person.name || person.email,
    email: person.email,
  }));
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MeetingDetailModal({ meetingId, isOpen, onOpenChange, onSuccess }: MeetingDetailModalProps) {
  const [_, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { openMartina } = useMartinaGlobal();

  const [outcomeText, setOutcomeText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [showFollowUpTask, setShowFollowUpTask] = useState(false);
  const [showFollowUpMeeting, setShowFollowUpMeeting] = useState(false);
  const [showEventAppointment, setShowEventAppointment] = useState(false);
  const [eventAppointmentData, setEventAppointmentData] = useState<any>(null);
  const [showEditMeeting, setShowEditMeeting] = useState(false);
  const [editMeetingData, setEditMeetingData] = useState<any>(null);
  const [showDealCreate, setShowDealCreate] = useState(false);
  const [followUpData, setFollowUpData] = useState<any>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showMergePicker, setShowMergePicker] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<number | null>(null);
  const [mergeScheduleSourceId, setMergeScheduleSourceId] = useState<number | null>(null);
  const [inviteDecision, setInviteDecision] = useState<'accepted' | 'declined' | 'counter' | 'dismissed' | null>(null);
  const [inviteDecisionEdited, setInviteDecisionEdited] = useState(false);
  const { requestClose: requestInviteDecisionClose, closeAfterSave: closeInviteDecisionAfterSave } = useUnsavedChangesGuard(
    inviteDecision !== null,
    inviteDecisionEdited,
    (open) => { if (!open) setInviteDecision(null); },
  );
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteDealId, setInviteDealId] = useState('');
  const [proposedStartTime, setProposedStartTime] = useState('');
  const [proposedEndTime, setProposedEndTime] = useState('');
  const [invitePreview, setInvitePreview] = useState<any | null>(null);

  // ── Fetch meeting ──────────────────────────────────────────────────────────

  const { data: meeting, isLoading, isError, error: meetingError, refetch: refetchMeeting } = useQuery<any>({
    queryKey: entityDetailKey('meetings', meetingId ?? ''),
    queryFn: () => apiRequest(`/api/meetings/${meetingId}`),
    enabled: !!meetingId && isOpen,
    select: (data) => {
      if (selectedStatus === null && data?.status) {
        setSelectedStatus(data.status);
        setOutcomeText(data.outcome || "");
      }
      return data;
    },
  });
  const operationalDescription = operationalMeetingDescription(meeting?.description);
  const outcomeDirty = Boolean(meeting) && selectedStatus !== null && (
    outcomeText !== (meeting.outcome || '')
    || (selectedStatus ?? meeting.status) !== meeting.status
  );
  const { requestClose, closeAfterSave } = useUnsavedChangesGuard(isOpen, outcomeDirty, onOpenChange);

  // ── Fetch entità collegate (con JWT tramite apiRequest) ────────────────────

  const { data: contact } = useQuery<any>({
    queryKey: [`/api/contacts/${meeting?.contactId}`],
    queryFn: () => apiRequest(`/api/contacts/${meeting.contactId}`),
    enabled: !!meeting?.contactId && isOpen,
    // Una relazione legacy puo' puntare a un contatto eliminato senza merge
    // tracciabile. Un 404 e' definitivo: non ripetere la stessa richiesta.
    retry: false,
  });

  const { data: company } = useQuery<any>({
    queryKey: [`/api/companies/${meeting?.companyId}`],
    queryFn: () => apiRequest(`/api/companies/${meeting.companyId}`),
    enabled: !!meeting?.companyId && isOpen,
  });

  const { data: deal } = useQuery<any>({
    queryKey: entityDetailKey('deals', meeting?.dealId ?? ''),
    queryFn: () => apiRequest(`/api/deals/${meeting.dealId}`),
    enabled: !!meeting?.dealId && isOpen,
  });

  const { data: lead } = useQuery<any>({
    queryKey: entityDetailKey('leads', meeting?.leadId ?? ''),
    queryFn: () => apiRequest(`/api/leads/${meeting.leadId}`),
    enabled: !!meeting?.leadId && isOpen,
  });

  const { data: linkedEvent } = useQuery<any>({
    queryKey: entityDetailKey('meetings', meeting?.eventId ?? ''),
    queryFn: () => apiRequest(`/api/meetings/${meeting.eventId}`),
    enabled: !!meeting?.eventId && isOpen,
  });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const createWalletMutation = useMutation({
    mutationFn: () => apiRequest('/api/travel/wallets/from-event', {
      method: 'POST', body: JSON.stringify({ eventId: meetingId, classification: 'business' }),
    }),
    onSuccess: (result: { walletId: number; created: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ['/api/travel/wallets'] });
      onOpenChange(false);
      navigate(`/travel?walletId=${result.walletId}&addItem=1`);
      toast({ title: result.created ? 'Wallet creato dall’Evento' : 'Wallet già collegato', description: 'Aggiungi ora volo, soggiorno o un’altra tappa; spese e documenti restano nello stesso Wallet.' });
    },
    onError: () => toast({ title: 'Wallet non creato', description: 'Verifica che l’Evento e le sue date siano validi. Nessun dato è stato duplicato.', variant: 'destructive' }),
  });

  const saveMutation = useMutation({
    mutationFn: (data: { status?: string; outcome?: string }) =>
      apiRequest(`/api/meetings/${meetingId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: (returnedMeeting) => {
      // Aggiornamento SINCRONO della cache per apparizione istantanea
      const existing = queryClient.getQueryData<any[]>(['/api/meetings']) || [];
      queryClient.setQueryData(['/api/meetings'], existing.map(m => m.id === returnedMeeting.id ? returnedMeeting : m));

      // Aggiorna anche la cache del dettaglio
      queryClient.setQueryData(entityDetailKey('meetings', meetingId ?? ''), returnedMeeting);

      // Invalidazione asincrona per sincronizzazione
      invalidateMeetingViews(queryClient, returnedMeeting.id);
      if (returnedMeeting.companyId) queryClient.invalidateQueries({ queryKey: ['/api/meetings/company', returnedMeeting.companyId] });
      if (returnedMeeting.contactId) queryClient.invalidateQueries({ queryKey: ['/api/meetings', { contactId: returnedMeeting.contactId }] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard'] });

      toast({ title: "Meeting aggiornato" });
      onSuccess?.();
    },
    onError: () => toast({ title: "Errore salvataggio", variant: "destructive" }),
  });

  /**
   * Risposta all'invito (§13.2). Passa da un endpoint dedicato, non da PATCH:
   * la decisione umana registra anche `inviteActedAt` e l'activity di origine,
   * e il rifiuto porta il meeting in `cancelled` senza cancellarlo.
   */
  const inviteResponseMutation = useMutation({
    mutationFn: (data: {
      decision: 'accepted' | 'declined' | 'counter' | 'dismissed';
      delivery: 'sent' | 'local_only';
      dealId?: number;
      message?: string;
      confirmationPhrase?: string;
      proposedStartTime?: string;
      proposedEndTime?: string;
    }) =>
      apiRequest(`/api/meetings/${meetingId}/invite-response`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (returnedMeeting) => {
      queryClient.setQueryData(entityDetailKey('meetings', meetingId ?? ''), returnedMeeting);
      const existing = queryClient.getQueryData<any[]>(['/api/meetings']) || [];
      // Una decisione "rifiutato" non deve aspettare il refetch per sparire:
      // il client condivide la stessa semantica operativa del server e non
      // lascia un falso impegno visibile fra click e risposta della query.
      const terminal = ['deleted', 'cancelled', 'ignored'].includes(String(returnedMeeting?.status ?? '').toLowerCase());
      queryClient.setQueryData(
        ['/api/meetings'],
        terminal
          ? existing.filter((meeting) => meeting.id !== returnedMeeting.id)
          : existing.map((meeting) => meeting.id === returnedMeeting.id ? returnedMeeting : meeting),
      );
      invalidateMeetingViews(queryClient, returnedMeeting.id);
      // La risposta chiude anche il promemoria di Martina: le superfici che lo
      // mostrano vanno rilette, altrimenti resta a schermo un obbligo gia'
      // assolto.
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard'] });
      const label = INVITE_STATUS_CONFIG[returnedMeeting?.inviteStatus]?.label ?? 'Aggiornato';
      toast({
        title: `Invito: ${label.toLowerCase()}`,
        description: returnedMeeting?.inviteResponseDelivery === 'local_only'
          ? 'Decisione registrata solo nel CRM: nessuna risposta è stata inviata.'
          : 'La risposta è stata inviata al mittente.',
      });
      closeInviteDecisionAfterSave();
      setInviteDecisionEdited(false);
      setInviteMessage('');
      setInviteDealId('');
      setProposedStartTime('');
      setProposedEndTime('');
      onSuccess?.();
    },
    onError: (error: Error, request) => toast({
      title: request.delivery === "local_only" ? "Decisione non registrata" : "Risposta non inviata",
      description: error.message || "Il meeting non è stato modificato.",
      variant: "destructive",
    }),
  });

  // L'invito verso terzi ha un flusso separato dall'RSVP: prima si legge
  // esattamente cosa partirà, poi il token della preview abilita un solo invio.
  const invitePreviewMutation = useMutation({
    mutationFn: () => apiRequest(`/api/meetings/${meetingId}/invite-preview`),
    onSuccess: (preview) => setInvitePreview(preview),
    onError: (error: Error) => toast({ title: 'Anteprima non disponibile', description: error.message, variant: 'destructive' }),
  });
  const sendInviteMutation = useMutation({
    mutationFn: () => apiRequest(`/api/meetings/${meetingId}/send-invite`, {
      method: 'POST',
      body: JSON.stringify({ previewToken: invitePreview?.previewToken }),
    }),
    onSuccess: (result: any) => {
      toast({ title: 'Invito inviato', description: `Destinatari: ${(result.recipients ?? []).join(', ')}` });
      setInvitePreview(null);
      if (meetingId) invalidateMeetingViews(queryClient, meetingId);
      refetchMeeting();
    },
    onError: (error: Error) => toast({ title: 'Invito non inviato', description: error.message, variant: 'destructive' }),
  });

  /**
   * Appuntamenti candidati alla fusione: gli altri appuntamenti dell'utente,
   * senza alcun punteggio di somiglianza. Il consolidamento automatico fonde
   * solo quando l'identita' e' dichiarata (stessa stanza di conferenza); quando
   * quella chiave manca — per esempio la notifica del calendario di Google, che
   * parla dello stesso evento da un altro link — a decidere e' Michele, e la
   * lista gli mostra le evidenze invece di suggerire una risposta.
   */
  const { data: mergeCandidates = [] } = useQuery<any[]>({
    queryKey: ['/api/meetings'],
    queryFn: () => apiRequest('/api/meetings'),
    enabled: showMergePicker && isOpen,
  });

  /**
   * Fusione di due appuntamenti: passa dalla facade unica dei merge, quindi
   * TUTTE le associazioni (attivita', debrief, osservazioni dell'invito,
   * notifiche, Stanze di Martina) seguono il sopravvissuto invece di restare
   * agganciate a un record morto (MASTER §3.8.5). Sopravvive quello scelto:
   * questo appuntamento resta memoria consultabile, non viene cancellato.
   */
  const mergeMutation = useMutation({
    mutationFn: ({ targetId, scheduleSourceId }: { targetId: number; scheduleSourceId: number }) =>
      apiRequest('/api/merge/meeting', {
        method: 'POST',
        body: JSON.stringify({
          targetId,
          duplicateId: meetingId,
          mergedData: { scheduleSourceId },
          strategy: 'manual',
          confirmation: 'manual_meeting_merge',
        }),
      }),
    onSuccess: (_result, { targetId }) => {
      const existing = queryClient.getQueryData<any[]>(['/api/meetings']) || [];
      queryClient.setQueryData(['/api/meetings'], existing.filter((m) => m.id !== meetingId));
      invalidateMeetingViews(queryClient, targetId);
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard'] });
      toast({ title: `Appuntamenti uniti`, description: `Resta l'appuntamento #${targetId}.` });
      setShowMergePicker(false);
      setMergeTargetId(null);
      setMergeScheduleSourceId(null);
      closeAfterSave();
      setSelectedStatus(null);
      setOutcomeText("");
      onSuccess?.();
    },
    onError: (error: any) => toast({
      title: 'Fusione non riuscita',
      description: error?.message ?? undefined,
      variant: 'destructive',
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiRequest(`/api/meetings/${meetingId}`, { method: 'DELETE' }),
    onSuccess: () => {
      // Rimozione SINCRONA dalla cache generale
      const existing = queryClient.getQueryData<any[]>(['/api/meetings']) || [];
      queryClient.setQueryData(['/api/meetings'], existing.filter(m => m.id !== meetingId));

      // Invalidazione asincrona per sincronizzazione
      invalidateMeetingViews(queryClient, meetingId);
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard'] });

      toast({ title: "Meeting eliminato" });
      closeAfterSave();
      setSelectedStatus(null);
      setOutcomeText("");
      onSuccess?.();
    },
    onError: () => toast({ title: "Errore eliminazione", variant: "destructive" }),
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleClose = () => {
    if (!requestClose(false)) return false;
    setSelectedStatus(null);
    setOutcomeText("");
    setConfirmDelete(false);
    return true;
  };

  const handleSave = () => {
    saveMutation.mutate({
      status: selectedStatus ?? meeting?.status,
      outcome: outcomeText,
    });
  };

  const handleOpenFollowUpTask = () => {
    setShowFollowUpTask(true);
  };

  const toDateTimeLocal = (value: string | Date | null | undefined) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
    return local.toISOString().slice(0, 16);
  };

  const openInviteResponse = (decision: 'accepted' | 'declined' | 'counter' | 'dismissed') => {
    setInviteDecisionEdited(false);
    setInviteDecision(decision);
    setInviteMessage('');
    setInviteDealId(meeting?.dealId ? String(meeting.dealId) : '');
    if (decision === 'counter' && meeting?.startTime && meeting?.endTime) {
      const originalStart = new Date(meeting.startTime);
      const originalEnd = new Date(meeting.endTime);
      const duration = Math.max(15 * 60_000, originalEnd.getTime() - originalStart.getTime());
      const proposalStart = new Date(originalStart.getTime() + 24 * 60 * 60_000);
      setProposedStartTime(toDateTimeLocal(proposalStart));
      setProposedEndTime(toDateTimeLocal(new Date(proposalStart.getTime() + duration)));
    }
  };

  const submitInviteResponse = (delivery: 'sent' | 'local_only') => {
    if (!inviteDecision) return;
    if (delivery === 'local_only' && inviteDecision === 'counter') return;
    inviteResponseMutation.mutate({
      decision: inviteDecision,
      delivery,
      // Il secondo click nel dialog ("Si, invia risposta") e' la conferma
      // esplicita. Il token continua a proteggere il writer comune da chiamate
      // che non hanno attraversato una scelta di consegna.
      ...(delivery === 'sent' && inviteDecision !== 'dismissed'
        ? { confirmationPhrase: inviteConfirmationPhraseFor(inviteDecision) }
        : {}),
      ...(inviteDealId ? { dealId: Number(inviteDealId) } : {}),
      ...(inviteMessage.trim() ? { message: inviteMessage.trim() } : {}),
      ...(inviteDecision === 'counter' ? {
        proposedStartTime: proposedStartTime ? new Date(proposedStartTime).toISOString() : undefined,
        proposedEndTime: proposedEndTime ? new Date(proposedEndTime).toISOString() : undefined,
      } : {}),
    });
  };

  // Chiude il detail panel, prepara i dati e apre SimpleMeetingModal
  const handleOpenFollowUpMeeting = () => {
    if (!handleClose()) return;
    const data = {
      title: meeting ? `Follow-up: ${meeting.title}` : "",
      contactId: meeting?.contactId ?? undefined,
      companyId: meeting?.companyId ?? undefined,
      dealId: meeting?.dealId ?? undefined,
      description: outcomeText || undefined,
      meetingType: meeting?.meetingType ?? "In-Person",
      status: "active",
      startTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      endTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000),
    };
    setFollowUpData(data);
    // Chiude prima il detail, poi apre il form nuovo meeting
    setTimeout(() => setShowFollowUpMeeting(true), 150);
  };

  const handleOpenEventAppointment = () => {
    if (!meeting || !handleClose()) return;
    const window = travelEventAppointmentWindow(meeting);
    if (!window) {
      toast({ title: 'Date dell’Evento non valide', description: 'Correggi prima inizio e fine dell’Evento.', variant: 'destructive' });
      return;
    }
    setEventAppointmentData({
      title: `${meeting.title} · appuntamento`,
      contactId: meeting.contactId ?? undefined,
      companyId: meeting.companyId ?? undefined,
      dealId: meeting.dealId ?? undefined,
      location: meeting.location ?? undefined,
      meetingType: 'In-Person',
      status: 'scheduled',
      eventId: meeting.id,
      ...window,
    });
    setTimeout(() => setShowEventAppointment(true), 150);
  };

  // Chiude il detail (sola lettura) e apre SimpleMeetingModal in edit mode
  // sullo STESSO meeting (initialData.id -> isEditMode -> PATCH).
  // Cattura `meeting` in uno stato locale PRIMA di chiudere: onOpenChange(false)
  // azzera meetingId nel genitore, la query dettaglio cambia
  // chiave e `meeting` torna undefined subito — stesso motivo per cui il
  // follow-up qui sopra usa `followUpData` invece del `meeting` live.
  const handleEditMeeting = () => {
    if (!handleClose()) return;
    setEditMeetingData(meeting);
    setTimeout(() => setShowEditMeeting(true), 150);
  };

  /**
   * Deal creato dal meeting (regola: Deal solo manuali — il form esistente
   * DealModal ha gia' salvato su azione esplicita dell'utente). Qui resta il
   * wiring canonico con endpoint esistenti, nessuna creazione automatica:
   * - il meeting diventa il record di origine (meetings.deal_id);
   * - le persone del meeting (contatto + partecipanti esterni) entrano come
   *   partecipanti ordinari del Deal ('Partecipante Deal', non Sinergie);
   * - il contesto resta la pagina meeting; "Apri deal" porta al modulo per
   *   completare i campi. Le email si collegano al Deal esistente dalla sua
   *   scheda o dal dettaglio email (contratto email↔deal invariato).
   */
  const handleDealSaved = async (savedDeal: any) => {
    if (!meeting?.id || !savedDeal?.id) return;
    try {
      await apiRequest(`/api/meetings/${meeting.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ dealId: savedDeal.id }),
      });
      const contactIds = new Set<number>();
      if (meeting.contactId) contactIds.add(Number(meeting.contactId));
      for (const participant of Array.isArray(meeting.participants) ? meeting.participants : []) {
        const contactId = Number(participant?.contactId);
        if (Number.isSafeInteger(contactId) && contactId > 0) contactIds.add(contactId);
      }
      // Bug E5: `external` e' un array di OGGETTI {email, name, contactId}, non
      // di id. `Number(oggetto)` restituiva NaN per ogni partecipante, quindi
      // la creazione del Deal dal meeting provava a collegare partecipanti
      // inesistenti. Solo chi ha gia' un contatto CRM puo' essere collegato.
      for (const person of externalAttendees(meeting.attendees)) {
        if (person.contactId) contactIds.add(person.contactId);
      }
      for (const contactId of contactIds) {
        try {
          await apiRequest(`/api/deals/${savedDeal.id}/participants`, {
            method: 'POST',
            body: JSON.stringify({ entityType: 'contact', entityId: contactId, suggestedContactIds: [] }),
          });
        } catch (participantError) {
          console.warn('[MeetingDetailModal] partecipante non collegato (duplicato o entita mancante):', contactId, participantError);
        }
      }
      invalidateMeetingViews(queryClient, meeting.id);
      queryClient.invalidateQueries({ queryKey: [`/api/deals/${savedDeal.id}/participants`] });
      toast({
        title: "Deal creato e collegato al meeting",
        description: `Origine e partecipanti del meeting sono stati collegati a "${savedDeal.name ?? 'Deal'}".`,
        action: (
          <ToastAction altText="Apri deal" onClick={() => navigate(`/deals/${savedDeal.id}`)}>
            Apri deal
          </ToastAction>
        ),
      });
    } catch (error) {
      toast({
        title: "Deal creato, collegamento incompleto",
        description: error instanceof Error ? error.message : "Il deal esiste ma meeting o partecipanti non sono stati collegati.",
        variant: "destructive",
      });
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const fmt = (iso: string, opts: Intl.DateTimeFormatOptions) =>
    new Date(iso).toLocaleDateString('it-IT', opts);
  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

  const meetingStart = meeting?.startTime ? new Date(meeting.startTime) : null;
  const meetingEnd = meeting?.endTime ? new Date(meeting.endTime) : null;
  const stayBoundary = isTravelStayLike(meeting) && meetingStart && meetingEnd
    ? travelStayBoundary(meetingStart, meetingEnd, meeting?.sourceTimeZone)
    : null;
  const isAllDayMeeting = Boolean(meetingStart && meetingEnd
    && meetingAllDayBoundary(meetingStart, meetingEnd, meeting?.sourceTimeZone || 'Europe/Rome')
    && (meeting?.timeResolution === 'all_day' || stayBoundary || meeting?.meetingType === 'Evento'));
  const spansMultipleCivilDays = Boolean(meetingStart && meetingEnd
    && meetingStart.toDateString() !== meetingEnd.toDateString());
  const displayMeetingType = stayBoundary ? 'Soggiorno' : meeting?.meetingType;
  const formatStayDate = (dateKey: string) => new Intl.DateTimeFormat('it-IT', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${dateKey}T12:00:00.000Z`));

  const currentStatus = selectedStatus ?? meeting?.status ?? 'active';
  const statusCfg = STATUS_CONFIG[currentStatus] ?? STATUS_CONFIG['active'];
  const inviteResponseWindow = meetingInviteResponseWindow(meeting?.inviteStatus, meeting?.endTime);
  const inviteStatusConfig = meeting?.inviteStatus ? INVITE_STATUS_CONFIG[meeting.inviteStatus] : undefined;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          className="w-full sm:w-[500px] sm:max-w-[500px] overflow-y-auto p-0 flex flex-col"
        >
          {/* Header */}
          <SheetHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <SheetTitle className="pr-10 text-lg font-semibold leading-tight sm:pr-0">
                {isLoading ? "Caricamento…" : meeting?.title ?? "Meeting"}
              </SheetTitle>
              {meeting && (
                <div className="flex w-full flex-wrap items-center gap-1 sm:w-auto sm:shrink-0 sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 gap-1.5"
                    aria-label={`Chiedi a Martina sul meeting ${meeting.title}`}
                    onClick={() => {
                      openMartina({
                        context: buildMartinaCalendarMeetingTurnContext({
                          meetingId: meeting.id,
                          title: meeting.title || "Meeting",
                          contact: meeting.contactId
                            ? { id: meeting.contactId, label: contact ? `${contact.firstName ?? ""} ${contact.lastName ?? ""}`.trim() || undefined : undefined }
                            : null,
                          company: meeting.companyId
                            ? { id: meeting.companyId, label: company?.name || undefined }
                            : null,
                          deal: meeting.dealId
                            ? { id: meeting.dealId, label: deal?.name || undefined }
                            : null,
                        }),
                      });
                    }}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Chiedi a Martina
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-11 shrink-0 gap-1.5 text-muted-foreground hover:text-foreground"
                    onClick={handleEditMeeting}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Modifica
                  </Button>
                </div>
              )}
            </div>
            <SheetDescription className="sr-only">Dettagli meeting</SheetDescription>
            {meeting && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-muted-foreground">
                  {TYPE_ICON[displayMeetingType] ?? <Calendar className="h-4 w-4" />}
                </span>
                <span className="text-sm text-muted-foreground">{displayMeetingType}</span>
                <Badge variant="outline" className={`text-xs flex items-center gap-1 ${statusCfg.color}`}>
                  {statusCfg.icon}
                  {statusCfg.label}
                </Badge>
                {linkedEvent && (
                  <Badge variant="outline" className="flex items-center gap-1 text-xs text-[color:var(--ma-primary)] border-[color:var(--ma-primary)]/30 bg-[color:var(--ma-primary)]/5">
                    <CalendarRange className="h-3 w-3" />
                    {linkedEvent.title}
                  </Badge>
                )}
              </div>
            )}
          </SheetHeader>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {isLoading && <p className="text-sm text-muted-foreground py-8 text-center">Caricamento…</p>}
            {isError && (
              <div role="alert" className="flex items-start gap-2 text-destructive text-sm py-4">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="space-y-2">
                  <p>{meetingDetailLoadError(meetingError)}</p>
                  <Button type="button" variant="outline" size="sm" onClick={() => void refetchMeeting()}>
                    Riprova
                  </Button>
                </div>
              </div>
            )}

            {meeting && (
              <>
                {meeting.googleCalendarEventId && (
                  <section className="rounded-lg border border-border bg-muted/30 p-3 text-sm" aria-label="Origine e sincronizzazione calendario">
                    <p className="font-medium text-foreground">Google Calendar · {meeting.googleCalendarSyncStatus === 'synced' ? 'sincronizzato' : meeting.googleCalendarSyncStatus === 'error' ? 'sincronizzazione non riuscita' : meeting.googleCalendarSyncStatus === 'pending' ? 'sincronizzazione in corso' : 'stato da verificare'}</p>
                    {meeting.inviteSource === 'google' && <p className="mt-1 text-muted-foreground">Questo impegno proviene da Google Calendar. Puoi correggere le date con “Modifica”. Se l'organizzatore è un'altra persona, la correzione riguarda la tua copia e un suo aggiornamento potrebbe sostituirla.</p>}
                    {meeting.googleCalendarSyncStatus === 'error' && <p className="mt-1 text-destructive">La modifica è nel CRM, ma non è stata confermata su Google Calendar.</p>}
                  </section>
                )}
                {/* Invito ricevuto (§13.2): richiesta di accordo con stato
                    binario. Prima del 1 ago 2026 il client non conosceva
                    `inviteStatus`, quindi tutto il lavoro di acquisizione
                    degli inviti restava invisibile e non azionabile. */}
                {meeting.inviteStatus && (
                  <section className="rounded-lg border p-4 space-y-3 bg-muted/40">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                        Invito ricevuto
                      </h3>
                      <Badge
                        variant="outline"
                        className={`max-w-full whitespace-normal text-left text-xs leading-snug ${inviteResponseWindow.expired ? 'bg-muted text-muted-foreground border-border' : inviteStatusConfig?.className ?? ''}`}
                      >
                        {inviteResponseWindow.expired ? 'Appuntamento concluso · risposta non registrata' : inviteStatusConfig?.label ?? meeting.inviteStatus}
                      </Badge>
                    </div>

                    {/* Origine: chi organizza e da quale email. */}
                    <div className="space-y-1.5 text-sm">
                      {meeting.inviteOrganizerEmail && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <User className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">Organizza {meeting.inviteOrganizerEmail}</span>
                        </div>
                      )}
                      {meeting.inviteMessageId && (
                        <button
                          type="button"
                          className="flex items-center gap-2 text-primary underline-offset-2 hover:underline"
                          onClick={() => {
                            if (!handleClose()) return;
                            navigate(`/emails?emailId=${meeting.inviteMessageId}`);
                          }}
                        >
                          <Link2 className="h-3.5 w-3.5 shrink-0" />
                          Apri l’email di origine
                        </button>
                      )}
                    </div>

                    {inviteResponseWindow.pending && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button
                          size="sm"
                          className="h-11 gap-1.5"
                          disabled={inviteResponseMutation.isPending}
                          onClick={() => openInviteResponse('accepted')}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Conferma
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-11 gap-1.5"
                          disabled={inviteResponseMutation.isPending}
                          onClick={() => openInviteResponse('counter')}
                        >
                          <Clock3 className="h-4 w-4" />
                          Proponi alternativa
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-11 gap-1.5"
                          disabled={inviteResponseMutation.isPending}
                          onClick={() => openInviteResponse('declined')}
                        >
                          <XCircle className="h-4 w-4" />
                          Rifiuta
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-11 gap-1.5"
                          disabled={inviteResponseMutation.isPending}
                          onClick={() => openInviteResponse('dismissed')}
                        >
                          <XCircle className="h-4 w-4" />
                          Chiudi
                        </Button>
                      </div>
                    )}

                    {/* Stesso evento visto due volte. Il consolidamento
                        automatico interviene solo quando l'identita' e'
                        dichiarata (stessa stanza di conferenza); senza quella
                        chiave nessuna euristica decide al posto di Michele —
                        decide lui, con le evidenze davanti. */}
                    <div className="pt-1 border-t">
                      {!showMergePicker ? (
                        <button
                          type="button"
                          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                          onClick={() => setShowMergePicker(true)}
                        >
                          È lo stesso appuntamento di un altro record? Unisci
                        </button>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground">
                            Scegli l’appuntamento che <strong>resta</strong>. Questo viene unito a
                            quello: partecipanti, email, promemoria e Stanza passano al
                            sopravvissuto, e questo record resta memoria consultabile.
                          </p>
                          <div className="max-h-56 overflow-y-auto rounded-lg border divide-y">
                            {mergeCandidates
                              .filter((candidate) => candidate.id !== meeting.id)
                              .map((candidate) => (
                                <button
                                  key={candidate.id}
                                  type="button"
                                  onClick={() => { setMergeTargetId(candidate.id); setMergeScheduleSourceId(null); }}
                                  className={`w-full text-left p-2.5 text-xs hover:bg-muted/60 ${
                                    mergeTargetId === candidate.id ? 'bg-muted' : ''
                                  }`}
                                >
                                  <div className="font-medium truncate">#{candidate.id} · {candidate.title}</div>
                                  <div className="text-muted-foreground truncate">
                                    {candidate.startTime ? fmt(candidate.startTime, {
                                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                                    }) : '—'}
                                    {candidate.inviteOrganizerEmail ? ` · ${candidate.inviteOrganizerEmail}` : ''}
                                    {candidate.location ? ` · ${candidate.location}` : ''}
                                  </div>
                                </button>
                              ))}
                            {mergeCandidates.filter((candidate) => candidate.id !== meeting.id).length === 0 && (
                              <div className="p-2.5 text-xs text-muted-foreground">
                                Nessun altro appuntamento disponibile.
                              </div>
                            )}
                          </div>
                          {mergeTargetId && (
                            <div className="rounded-lg border p-2.5 space-y-2">
                              <p className="text-xs font-medium">Quale orario e fonte invito devono restare?</p>
                              {[
                                { id: meeting.id, label: `Questo appuntamento (#${meeting.id})`, item: meeting },
                                { id: mergeTargetId, label: `Appuntamento scelto (#${mergeTargetId})`, item: mergeCandidates.find((candidate) => candidate.id === mergeTargetId) },
                              ].map(({ id, label, item }) => item && (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => setMergeScheduleSourceId(id)}
                                  className={`w-full rounded-md border p-2 text-left text-xs ${mergeScheduleSourceId === id ? 'border-primary bg-primary/5' : 'hover:bg-muted/60'}`}
                                >
                                  <div className="font-medium">{label}</div>
                                  <div className="text-muted-foreground">{item.startTime ? fmt(item.startTime, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Orario assente'}{item.endTime ? ` → ${fmtTime(item.endTime)}` : ''}</div>
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-11"
                              onClick={() => { setShowMergePicker(false); setMergeTargetId(null); setMergeScheduleSourceId(null); }}
                            >
                              Annulla
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                <MartinaOutputRating
                  outputType="meeting"
                  outputId={meeting.id}
                  label="Quanto e' importante questo appuntamento?"
                  context={{ surface: 'meeting_detail' }}
                />

                {/* Data e orario */}
                <section>
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">Data e orario</h3>
                  <div className="space-y-2 text-sm">
                    {stayBoundary ? (
                      <div className="rounded-lg border border-border bg-muted/30 p-3">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="flex items-start gap-3">
                            <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <div><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Check-in</p><p>{formatStayDate(stayBoundary.checkInDate)}</p></div>
                          </div>
                          <div className="flex items-start gap-3">
                            <CalendarRange className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <div><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Check-out</p><p>{formatStayDate(stayBoundary.checkOutDate)}</p></div>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border pt-3">
                          <Hotel className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="font-medium">{stayBoundary.nights} {stayBoundary.nights === 1 ? 'notte' : 'notti'}</span>
                          <span className="text-xs text-muted-foreground">Il check-out è il termine esclusivo del soggiorno.</span>
                        </div>
                      </div>
                    ) : <>
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span>
                          {meeting.startTime
                            ? fmt(meeting.startTime, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
                            : "—"}
                          {spansMultipleCivilDays && meeting.endTime
                            ? ` → ${fmt(meeting.endTime, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}`
                            : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span>
                          {isAllDayMeeting
                            ? 'Tutto il giorno'
                            : <>
                                {meeting.startTime ? fmtTime(meeting.startTime) : "—"}
                                {meeting.endTime ? ` → ${fmtTime(meeting.endTime)}` : ""}
                              </>}
                        </span>
                      </div>
                    </>}
                    {/* Doppio orario (§13.2, Michele 20/08): quando l'invito e'
                        arrivato in un fuso diverso dal mio, entrambi gli
                        orari devono essere ben visibili fianco a fianco, non
                        un accenno piccolo — cosi' un invito per le 9:00
                        California si legge subito come 18:00 qui. */}
                    {meeting.startTime && meeting.sourceTimeZone
                      && timeInZone(meeting.startTime, meeting.sourceTimeZone)
                      && timeInZone(meeting.startTime, meeting.sourceTimeZone) !== fmtTime(meeting.startTime) && (
                      <div className="flex items-start gap-3 border border-[color:var(--ma-primary)]/25 bg-[color:var(--ma-primary)]/5 p-3">
                        <Globe className="h-4 w-4 shrink-0 text-[color:var(--ma-primary)] mt-0.5" />
                        <div className="grid gap-1.5 sm:grid-cols-2 sm:gap-4 flex-1 min-w-0">
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Invito ricevuto per le</p>
                            <p className="font-semibold text-foreground">{timeInZone(meeting.startTime, meeting.sourceTimeZone)} · {zoneLabel(meeting.sourceTimeZone)}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Ti blocca il calendario alle</p>
                            <p className="font-semibold text-foreground">{fmtTime(meeting.startTime)} qui</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {meeting.location && (
                      <div className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                        {/^(https?:\/\/)/i.test(String(meeting.location)) ? (
                          <a href={meeting.location} target="_blank" rel="noreferrer" className="truncate text-primary underline-offset-2 hover:underline" onClick={(event) => event.stopPropagation()}>
                            {meeting.location}
                          </a>
                        ) : <span>{meeting.location}</span>}
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <Users className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <span>
                          {canonicalMeetingParticipants(meeting).length || countAttendees(meeting.attendees)}
                          {(canonicalMeetingParticipants(meeting).length || countAttendees(meeting.attendees)) === 1 ? ' partecipante' : ' partecipanti'}
                        </span>
                        {/* Il conteggio da solo non e' verificabile: se sbaglia,
                            l'elenco lo rende evidente invece di nasconderlo. */}
                        {canonicalMeetingParticipants(meeting).length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {canonicalMeetingParticipants(meeting).map((person, index) => (
                              <li key={`${person.email ?? person.label}-${index}`} className="text-xs text-muted-foreground truncate">
                                {person.email && person.label !== person.email ? `${person.label} — ${person.email}` : person.label}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </section>

                <Separator />

                {/* Collegato a */}
                <section>
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">Collegato a</h3>
                  {!contact && !company && !deal && !lead && !meeting.contactId && !meeting.companyId && !meeting.dealId && !meeting.leadId && !meeting.synergyId && (
                    <p className="text-sm text-muted-foreground italic">Nessuna entità collegata</p>
                  )}
                  {(meeting.contactId || meeting.companyId || meeting.dealId || meeting.leadId || meeting.synergyId) && (
                    <div className="space-y-2">
                      {meeting.leadId && (
                        <button
                          onClick={() => { if (handleClose()) navigate(`/leads/${meeting.leadId}`); }}
                          className="flex items-center gap-3 text-sm w-full text-left group"
                        >
                          <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-primary group-hover:underline flex-1">
                            {lead ? `${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim() : 'Lead da valutare'}
                          </span>
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        </button>
                      )}
                      {meeting.contactId && (
                        contact ? (
                          <button
                            onClick={() => { if (handleClose()) navigate(`/contacts/${meeting.contactId}`); }}
                            className="flex items-center gap-3 text-sm w-full text-left group"
                          >
                          <User className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-primary group-hover:underline flex-1">
                            {`${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || 'Contatto'}
                          </span>
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                          </button>
                        ) : (
                          <div className="flex items-center gap-3 text-sm text-muted-foreground" role="status">
                            <User className="h-4 w-4 shrink-0" />
                            <span>{'Contatto collegato non disponibile'}</span>
                          </div>
                        )
                      )}
                      {meeting.companyId && (
                        <button
                          onClick={() => { if (handleClose()) navigate(`/companies/${meeting.companyId}`); }}
                          className="flex items-center gap-3 text-sm w-full text-left group"
                        >
                          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-primary group-hover:underline flex-1">
                            {company?.name ?? 'Azienda non risolta'}
                          </span>
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        </button>
                      )}
                      {meeting.dealId && (
                        <button
                          onClick={() => { if (handleClose()) navigate(`/deals/${meeting.dealId}`); }}
                          className="flex items-center gap-3 text-sm w-full text-left group"
                        >
                          <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-primary group-hover:underline flex-1">
                            {deal?.title ?? deal?.name ?? 'Opportunita non risolta'}
                          </span>
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        </button>
                      )}
                      {meeting.synergyId && (
                        <div className="flex items-center gap-3 text-sm w-full">
                          <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="flex-1 text-muted-foreground">
                            {meeting.synergyName || `Sinergia #${meeting.synergyId}`}
                          </span>
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 border border-amber-200">lock</span>
                        </div>
                      )}
                    </div>
                  )}
                </section>

                <Separator />

                {/* Descrizione */}
                {operationalDescription && (
                  <>
                    <section>
                      <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-2">Descrizione</h3>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{operationalDescription}</p>
                    </section>
                    <Separator />
                  </>
                )}

                {/* Esito */}
                <section>
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">Esito appuntamento</h3>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {[
                      { key: "active",    label: "Pianificato", icon: <Clock3 className="h-4 w-4" />,       border: "border-blue-300 hover:bg-blue-50" },
                      { key: "completed", label: "Completato",  icon: <CheckCircle2 className="h-4 w-4" />, border: "border-green-300 hover:bg-green-50" },
                      { key: "postponed", label: "Posticipato", icon: <Clock className="h-4 w-4" />,         border: "border-amber-300 hover:bg-amber-50" },
                      { key: "cancelled", label: "Annullato",   icon: <XCircle className="h-4 w-4" />,       border: "border-red-300 hover:bg-red-50" },
                    ].map(({ key, label, icon, border }) => (
                      <button
                        key={key}
                        onClick={() => setSelectedStatus(key)}
                        className={`flex flex-col items-center gap-1 p-3 border-2 text-xs font-medium transition-all
                          ${currentStatus === key
                            ? `${STATUS_CONFIG[key].color} border-current`
                            : `border-border text-muted-foreground ${border}`
                          }`}
                      >
                        {icon}
                        {label}
                      </button>
                    ))}
                  </div>
                </section>

                {/* Note esito */}
                <section>
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-2">Note esito / cosa è stato deciso</h3>
                  <VoiceAssistantTextarea
                    placeholder="Es: Accordato preventivo entro venerdì. Cliente interessato al piano Pro..."
                    value={outcomeText}
                    onValueChange={setOutcomeText}
                    fieldLabel="Note esito / cosa è stato deciso"
                    martinaContext={{
                      terminalType: "entity",
                      entityType: "meeting",
                      entityId: meetingId ? String(meetingId) : undefined,
                      route: meetingId ? `/meetings/${meetingId}` : "/meetings",
                      parentEntityType: meeting?.contactId ? "contact" : meeting?.companyId ? "company" : meeting?.dealId ? "deal" : undefined,
                      parentEntityId: meeting?.contactId ? String(meeting.contactId) : meeting?.companyId ? String(meeting.companyId) : meeting?.dealId ? String(meeting.dealId) : undefined,
                    }}
                    rows={4}
                    className="text-sm resize-none"
                  />
                </section>

                <Separator />

                {canCreateTravelWalletFromEvent(meeting) && (
                  <section>
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-2">Viaggio, badge e spese</h3>
                    <p className="mb-3 text-xs text-muted-foreground">Il Wallet raccoglie voli, soggiorni, documenti e spese. Gli appuntamenti restano nel Calendario e sono collegati a questo Evento.</p>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                      <Button variant="outline" size="sm" className="gap-2" onClick={() => createWalletMutation.mutate()} disabled={createWalletMutation.isPending}>
                        <Briefcase className="h-4 w-4" />
                        {createWalletMutation.isPending ? 'Apro il Wallet…' : 'Crea o apri Wallet viaggio'}
                      </Button>
                      <Button variant="outline" size="sm" className="gap-2" onClick={handleOpenEventAppointment}>
                        <Calendar className="h-4 w-4" />
                        Nuovo appuntamento nell’Evento
                      </Button>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">Dal Wallet si apre subito “Aggiungi tappa”. Se esiste già, viene riusato senza duplicarlo.</p>
                  </section>
                )}

                {/* Invito esplicito: mai come side effect del salvataggio. */}
                {!meeting.inviteOrganizerEmail && (
                  <section>
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-2">Invito calendario</h3>
                    <p className="mb-3 text-xs text-muted-foreground">Apri l’anteprima per verificare contenuto e partecipanti prima dell’invio.</p>
                    <Button variant="outline" size="sm" className="gap-2" onClick={() => invitePreviewMutation.mutate()} disabled={invitePreviewMutation.isPending}>
                      <Mail className="h-4 w-4" />
                      {invitePreviewMutation.isPending ? 'Preparo anteprima…' : 'Anteprima invito'}
                    </Button>
                  </section>
                )}

                <Separator />

                {/* Follow-up */}
                <section>
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">Follow-up</h3>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 justify-start gap-2"
                      onClick={handleOpenFollowUpTask}
                    >
                      <Plus className="h-4 w-4" />
                      Crea Task follow-up
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 justify-start gap-2"
                      onClick={handleOpenFollowUpMeeting}
                    >
                      <Calendar className="h-4 w-4" />
                      Nuovo meeting follow-up
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 justify-start gap-2"
                      onClick={() => setShowDealCreate(true)}
                    >
                      <Briefcase className="h-4 w-4" />
                      Crea Deal
                    </Button>
                  </div>
                </section>

                <Separator />

                {/* Elimina */}
                <section>
                  {confirmDelete ? (
                    <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 text-sm">
                      <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                      <span className="flex-1 text-red-700">Eliminare definitivamente?</span>
                      <Button size="sm" variant="destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
                        Elimina
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>No</Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(true)}
                      className="flex items-center gap-2 text-xs text-destructive hover:opacity-80 transition-opacity"
                    >
                      <Trash2 className="h-3 w-3" />
                      Elimina meeting
                    </button>
                  )}
                </section>
              </>
            )}
          </div>

          {/* Footer */}
          {meeting && (
            <div className="px-6 py-4 border-t bg-background flex gap-3">
              {showMergePicker ? (
                <Button
                  className="flex-1"
                  onClick={() => mergeTargetId && mergeScheduleSourceId && mergeMutation.mutate({ targetId: mergeTargetId, scheduleSourceId: mergeScheduleSourceId })}
                  disabled={!mergeTargetId || !mergeScheduleSourceId || mergeMutation.isPending}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {mergeMutation.isPending
                    ? 'Unione in corso…'
                    : mergeTargetId && mergeScheduleSourceId
                      ? `Conferma unione in #${mergeTargetId}`
                      : 'Seleziona record e orario da mantenere'}
                </Button>
              ) : (
                <Button className="flex-1" onClick={handleSave} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Salvataggio…" : "Salva modifiche"}
                </Button>
              )}
              <Button variant="outline" onClick={handleClose}>Chiudi</Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={invitePreview !== null} onOpenChange={(open) => !open && !sendInviteMutation.isPending && setInvitePreview(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Anteprima invito calendario</DialogTitle>
            <DialogDescription>Verifica destinatari e contenuto: l’invio parte solo dalla conferma qui sotto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {invitePreview?.blockedReason ? (
              <p className="border border-amber-300 bg-amber-50 p-3 text-amber-900">{invitePreview.blockedReason}</p>
            ) : (
              <>
                <div><span className="font-medium">Canale:</span> {invitePreview?.channel === 'google' ? 'Google Calendar' : 'CRM email'}</div>
                <div><span className="font-medium">Destinatari:</span> {(invitePreview?.recipients ?? []).join(', ') || 'Nessuno'}</div>
                <div><span className="font-medium">Oggetto:</span> {invitePreview?.subject}</div>
                <p className="whitespace-pre-wrap border border-border bg-muted/40 p-3">{invitePreview?.body}</p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvitePreview(null)} disabled={sendInviteMutation.isPending}>Annulla</Button>
            {!invitePreview?.blockedReason && (
              <Button onClick={() => sendInviteMutation.mutate()} disabled={sendInviteMutation.isPending || !(invitePreview?.recipients?.length)}>
                {sendInviteMutation.isPending ? 'Invio…' : 'Conferma invio'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteDecision !== null} onOpenChange={(open) => !open && !inviteResponseMutation.isPending && requestInviteDecisionClose(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {inviteDecision === 'dismissed'
                ? 'Chiudi l’invito'
                : inviteDecision === 'accepted'
                ? 'Conferma l’invito'
                : inviteDecision === 'declined'
                  ? 'Rifiuta l’invito'
                  : 'Proponi un’alternativa'}
            </DialogTitle>
            <DialogDescription>
              {inviteDecision === 'dismissed'
                ? 'L’invito resta visibile nello storico e nel calendario; Martina non lo riproporrà e non viene inviato nulla all’organizzatore.'
                : inviteDecision === 'counter'
                  ? `La proposta ha effetto solo se viene inviata all’organizzatore. L’orario ufficiale non cambierà finché non arriva una nuova conferma.`
                  : 'Vuoi inviare una risposta all’organizzatore? Puoi anche registrare la decisione soltanto nel CRM.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
              <p className="font-medium">{meeting?.title ?? 'Invito calendario'}</p>
              <p className="mt-1 text-muted-foreground">Destinatario: {meeting?.inviteOrganizerEmail ?? 'organizzatore non disponibile'}</p>
              <p className="mt-2 text-muted-foreground">“Solo nel CRM” non invia email, RSVP o ICS. È la scelta adatta per notifiche automatiche, portali e inviti gestiti tramite link esterni.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="invite-deal">Opportunità collegata (facoltativa)</label>
              <CrmRecordAutocomplete
                id="invite-deal"
                entityType="deal"
                value={inviteDealId}
                onValueChange={(value) => { setInviteDecisionEdited(true); setInviteDealId(value); }}
                placeholder="Cerca un’opportunità esistente…"
              />
              <p className="text-xs text-muted-foreground">Collega solo un Deal già esistente: non ne viene creato alcuno.</p>
            </div>

            {inviteDecision === 'counter' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium" htmlFor="invite-proposed-start">Nuovo inizio</label>
                  <input id="invite-proposed-start" type="datetime-local" value={proposedStartTime} onChange={(event) => { setInviteDecisionEdited(true); setProposedStartTime(event.target.value); }} className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium" htmlFor="invite-proposed-end">Nuova fine</label>
                  <input id="invite-proposed-end" type="datetime-local" value={proposedEndTime} onChange={(event) => { setInviteDecisionEdited(true); setProposedEndTime(event.target.value); }} className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="invite-message">Messaggio al mittente o nota interna (facoltativa)</label>
              <textarea id="invite-message" value={inviteMessage} onChange={(event) => { setInviteDecisionEdited(true); setInviteMessage(event.target.value); }} rows={3} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Se scegli solo nel CRM, questa resta una nota interna per Martina…" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" disabled={inviteResponseMutation.isPending} onClick={() => requestInviteDecisionClose(false)}>Annulla</Button>
            {inviteDecision === 'dismissed' ? (
              <Button variant="secondary" disabled={inviteResponseMutation.isPending} onClick={() => submitInviteResponse('local_only')}>
                Chiudi
              </Button>
            ) : inviteDecision !== 'counter' && (
              <Button variant="secondary" disabled={inviteResponseMutation.isPending || !inviteDecision} onClick={() => submitInviteResponse('local_only')}>
                {inviteResponseMutation.isPending && inviteResponseMutation.variables?.delivery === 'local_only' ? 'Salvataggio nel CRM…' : 'No, solo nel CRM'}
              </Button>
            )}
            {inviteDecision !== 'dismissed' && (
              <Button disabled={inviteResponseMutation.isPending || !inviteDecision} onClick={() => submitInviteResponse('sent')}>
                {inviteResponseMutation.isPending && inviteResponseMutation.variables?.delivery === 'sent' ? 'Invio in corso…' : inviteDecision === 'counter' ? 'Sì, invia proposta' : 'Sì, invia risposta'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task follow-up — pre-popolato con le stesse dipendenze del meeting */}
      {showFollowUpTask && meeting && (
        <TaskModal
          isOpen={showFollowUpTask}
          onClose={() => setShowFollowUpTask(false)}
          initialData={{
            title: `Follow-up: ${meeting.title}`,
            description: [
              operationalDescription ? `📋 ${operationalDescription}` : null,
              outcomeText ? `✅ Esito: ${outcomeText}` : null,
            ].filter(Boolean).join('\n\n') || undefined,
            contactId: meeting.contactId ?? null,
            companyId: meeting.companyId ?? null,
            dealId: meeting.dealId ?? null,
            synergyId: meeting.synergyId ?? null,
          }}
          contactId={meeting.contactId ?? undefined}
          companyId={meeting.companyId ?? undefined}
          dealId={meeting.dealId ?? undefined}
          synergyId={meeting.synergyId ?? undefined}
        />
      )}

      {/* Nuovo meeting follow-up — aperto DOPO che il detail si è chiuso */}
      {showFollowUpMeeting && followUpData && (
        <SimpleMeetingModal
          isOpen={showFollowUpMeeting}
          onOpenChange={(open) => {
            setShowFollowUpMeeting(open);
            if (!open) setFollowUpData(null);
          }}
          initialData={followUpData}
        />
      )}

      {/* Appuntamento contenuto nell'Evento: resta un Meeting distinto e conserva eventId. */}
      {showEventAppointment && eventAppointmentData && (
        <SimpleMeetingModal
          isOpen={showEventAppointment}
          onOpenChange={(open) => {
            setShowEventAppointment(open);
            if (!open) {
              setEventAppointmentData(null);
              onSuccess?.();
            }
          }}
          initialData={eventAppointmentData}
        />
      )}

      {/* Modifica meeting corrente — initialData.id presente -> SimpleMeetingModal
          entra in edit mode (PATCH sullo stesso meeting, non crea un nuovo record) */}
      {showEditMeeting && editMeetingData && (
        <SimpleMeetingModal
          isOpen={showEditMeeting}
          onOpenChange={(open) => {
            setShowEditMeeting(open);
            if (!open) {
              setEditMeetingData(null);
              onSuccess?.();
            }
          }}
          initialData={editMeetingData}
        />
      )}

      {/* Crea Deal dal meeting — form esistente in drawer, prefill dal record;
          origine (meetings.deal_id), persone e source='meeting' nel wiring onSaved */}
      {showDealCreate && meeting && (
        <DealModal
          open={showDealCreate}
          onOpenChange={setShowDealCreate}
          initialData={{
            name: meeting.title,
            contactId: meeting.contactId ?? null,
            companyId: meeting.companyId ?? null,
            branchId: meeting.branchId ?? null,
            notes: outcomeText || meeting.outcome || null,
            source: 'meeting',
          }}
          onSaved={handleDealSaved}
        />
      )}
    </>
  );
}
